import { ACCOUNT_STATUS_STRIKE_PAUSED } from './lateCancellationStrikes';

export const normalizeNearbyClassIds = (classes = []) => {
  const seen = new Set();

  return classes.reduce((ids, classId) => {
    const normalized = String(classId || '').trim();
    if (!normalized || seen.has(normalized)) return ids;

    seen.add(normalized);
    ids.push(normalized);
    return ids;
  }, []);
};

export const chunkClassIdsForFirestore = (classIds = [], chunkSize = 10) => {
  const chunks = [];

  for (let index = 0; index < classIds.length; index += chunkSize) {
    chunks.push(classIds.slice(index, index + chunkSize));
  }

  return chunks;
};

export const getLatLngFromLocation = (location) => {
  if (!location) return null;

  const lat = Number(location.latitude ?? location.lat);
  const lng = Number(location.longitude ?? location.lng);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { lat, lng };
};

const getOpportunityPosition = (classData = {}, facility = {}) => (
  getLatLngFromLocation(facility.location)
  || getLatLngFromLocation(facility.Location)
  || getLatLngFromLocation({
    latitude: facility.latitude ?? facility.Latitude,
    longitude: facility.longitude ?? facility.Longitude,
  })
  || getLatLngFromLocation(classData.location)
  || getLatLngFromLocation(classData.Location)
  || getLatLngFromLocation(classData.FacilityLocation)
  || getLatLngFromLocation({
    latitude: classData.FacilityLatitude ?? classData.latitude ?? classData.Latitude,
    longitude: classData.FacilityLongitude ?? classData.longitude ?? classData.Longitude,
  })
);

export const getNearbyOpportunityProfileIssue = (instructor = {}) => {
  if (!getLatLngFromLocation(instructor?.location)) {
    return 'Add your home location before browsing nearby opportunities.';
  }

  return '';
};

export const buildOpportunityMapItems = (opportunities = [], facilitiesById = {}) => (
  opportunities.reduce((items, classData) => {
    const facility = facilitiesById[classData.FacilityID] || {};
    const position = getOpportunityPosition(classData, facility);

    if (!position) return items;

    items.push({
      id: classData.id,
      classData,
      facilityName: facility.FacilityName || facility.FirstName || classData.FacilityName || 'Facility',
      facilityAddress: facility.Address || classData.Address || 'Address unavailable',
      position,
    });

    return items;
  }, [])
);

export const areOpportunityFacilitiesLoaded = (opportunities = [], facilitiesById = {}) => (
  opportunities.every((classData) => (
    !classData?.FacilityID
    || Object.prototype.hasOwnProperty.call(facilitiesById, classData.FacilityID)
  ))
);

export const getOpportunityHydrationState = ({
  nearbyClassIdsLoaded = false,
  isLoadingClassDocs = false,
  isLoadingFacilities = false,
  opportunities = [],
} = {}) => {
  const isHydrating = !nearbyClassIdsLoaded || isLoadingClassDocs || isLoadingFacilities;

  return {
    isHydrating,
    showEmptyState: nearbyClassIdsLoaded && !isHydrating && opportunities.length === 0,
  };
};

const CLAIMABLE_STATUSES = new Set(['2', '3']);

export const isClassClaimableOpportunity = (classData = {}, nowMs = Date.now()) => (
  Number(classData.EndDate || 0) >= nowMs &&
  CLAIMABLE_STATUSES.has(String(classData.classStatus)) &&
  !classData.AssignedInstructorID
);

export const filterClaimableOpportunityClasses = (classes = [], nowMs = Date.now()) => (
  classes.filter((classData) => isClassClaimableOpportunity(classData, nowMs))
);

export const validateOpportunityClaim = ({
  classData,
  stripeComplete,
  instructorAccountStatus = '',
  nowMs = Date.now(),
}) => {
  if (!stripeComplete) {
    throw new Error('Complete Stripe setup to claim classes.');
  }

  if (String(instructorAccountStatus || '').trim() === ACCOUNT_STATUS_STRIKE_PAUSED) {
    throw new Error('Your account is paused from claiming new classes. Please request a review from your profile if you believe this needs another look.');
  }

  if (!classData) {
    throw new Error('Class no longer exists.');
  }

  if (classData.AssignedInstructorID) {
    throw new Error('This class has already been claimed.');
  }

  if (!isClassClaimableOpportunity(classData, nowMs)) {
    throw new Error('This class is no longer available.');
  }
};
